import { Dialog, DialogTitle, DialogContent, Box, FormControl, RadioGroup, FormControlLabel, Radio, DialogActions, Button, Paper, Stack, Typography, Backdrop, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, memo, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import {
  OutsourcingContractLegRunPdfPreviewRequestEntity,
  OutsourcingContractLegRunPdfRequestEntity,
} from 'src/entities/OutsourcingContractLegRunPdf.entity';
import { OutsourcingContractPdfGenerationSetting } from 'src/entities/OutsourcingContractPdf.entity';
import { useMutationOutsourcingContractLegRunPdf, useOutsourcingContractLegRunsQuery } from 'src/hooks/useOutsourcingContractLegRunPdf.request';
import { useOutsourcingContractSettingQuery } from 'src/hooks/useOutsourcingContractSetting.request';
import stringUtil from 'src/utils/string.util';

import DebouncedTextField from '../OutsourcingContractSettingsPresenter/DebouncedTextFieldProps';

type Action =
  | { type: 'SET_FIELD', key: keyof OutsourcingContractPdfGenerationSetting, legRunId: number, value: string | File | boolean }
  | { type: 'INIT_LEG_RUN', legRunId: number, value: OutsourcingContractPdfGenerationSetting };

const formReducer = (state: Record<number, OutsourcingContractPdfGenerationSetting>, action: Action): Record<number, OutsourcingContractPdfGenerationSetting> => {
  switch (action.type) {
    case 'SET_FIELD': {
      return {
        ...state,
        [action.legRunId]: {
          ...state[action.legRunId],
          [action.key]: action.value,
        },
      };
    }
    case 'INIT_LEG_RUN': {
      // 既に初期化済み（ユーザー編集を含む）なら上書きしない。再取得で編集が消えるのを防ぐ。
      if (state[action.legRunId]) return state;

      return { ...state, [action.legRunId]: action.value };
    }
    default:
      return state;
  }
};

type Props = {
  downloadOutsourcingPdfSettingsIsOpen: boolean;
  handlePdfSettingsClose: () => void;
  shipmentOrderIds: number[];
};

const PdfSettingDialog: FC<Props> = memo(({
  downloadOutsourcingPdfSettingsIsOpen,
  handlePdfSettingsClose,
  shipmentOrderIds,
}) => {
  const licenseContext = useContext(LicenseContext);
  const { enqueueSnackbar } = useSnackbar();

  const { data: setting, isLoading: settingIsLoading } = useOutsourcingContractSettingQuery(licenseContext.config?.selected_company_id || licenseContext.config?.company_id, downloadOutsourcingPdfSettingsIsOpen);
  const {
    data: legRuns,
    isLoading: legRunsIsLoading,
    isFetching: legRunsIsFetching,
    isError: legRunsIsError,
  } = useOutsourcingContractLegRunsQuery(shipmentOrderIds, downloadOutsourcingPdfSettingsIsOpen);
  const { generate, create } = useMutationOutsourcingContractLegRunPdf();

  const legRunIds = useMemo(() => (legRuns ?? []).map((legRun) => legRun.legRunId), [legRuns]);

  const [currentLegRunId, setCurrentLegRunId] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [pdfGenerationType, setPdfGenerationType] = useState<'single' | 'multiple'>('single');
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const defaultSettings: OutsourcingContractPdfGenerationSetting = useMemo(() => ({
    companyId: licenseContext.config?.selected_company_id || licenseContext.config?.company_id,
    introduction: '',
    aboutUnitPrice: '',
    paymentTerm: '',
    companyName: '',
    companyAddress: '',
    companyPhoneNo: '',
    companyFaxNo: '',
    freeTextOneTitle: '',
    freeTextOneContent: '',
    freeTextTwoTitle: '',
    freeTextTwoContent: '',
    freeTextThreeContent: '',
    freeTextFourContent: '',
    imageFile: null,
    imageUrl: '',
    branchName: '',
    contactPerson: '',
    requiredMaterials: '',
    hasAttachment: false,
  }), [licenseContext.config]);

  // ダイアログを開いた時、または現在の leg_run がリストから消えた時のみ先頭へ初期化する。
  // legRuns の再取得で参照が変わっただけでページ送りがリセットされるのを防ぎ、閉じた時はクリアする。
  useEffect(() => {
    if (!downloadOutsourcingPdfSettingsIsOpen) {
      setCurrentLegRunId(0);
      return;
    }
    if (legRunIds.length > 0 && (!currentLegRunId || !legRunIds.includes(currentLegRunId))) {
      setCurrentLegRunId(legRunIds[0]);
    }
  }, [legRunIds, downloadOutsourcingPdfSettingsIsOpen, currentLegRunId]);

  const [formState, dispatch] = useReducer(formReducer, {});
  const [debouncedState, dispatchDebounced] = useReducer(formReducer, {});

  useEffect(() => {
    if (!downloadOutsourcingPdfSettingsIsOpen) return;
    if (settingIsLoading) return;
    if (!setting) return;
    if (legRunIds.length === 0) return;

    const defaultValues = { ...defaultSettings, ...setting } as OutsourcingContractPdfGenerationSetting;

    // 未初期化の leg_run のみ初期値を投入する（再取得で legRunIds の参照が変わっても編集を保持）。
    legRunIds.forEach((legRunId) => {
      dispatch({ type: 'INIT_LEG_RUN', legRunId, value: defaultValues });
      dispatchDebounced({ type: 'INIT_LEG_RUN', legRunId, value: defaultValues });
    });
  }, [defaultSettings, legRunIds, setting, settingIsLoading, downloadOutsourcingPdfSettingsIsOpen]);

  useEffect(() => {
    if (!downloadOutsourcingPdfSettingsIsOpen) return;

    const generatePreview = async () => {
      if (settingIsLoading || !debouncedState[currentLegRunId]) {
        setPreviewHtml('');
        return;
      }
      const entity: OutsourcingContractLegRunPdfPreviewRequestEntity = {
        outsourcingContractSettings: debouncedState[currentLegRunId],
        currentLegRunId,
        outsourcingContractSettingChange: null,
        preview: true,
      };

      const result = await create.mutateAsync(entity);
      setPreviewHtml(String(result.data));
    };

    void generatePreview();
  }, [currentLegRunId, debouncedState, settingIsLoading, downloadOutsourcingPdfSettingsIsOpen]);

  // localStorage からの初期値読み込み
  useEffect(() => {
    const savedType = localStorage.getItem('pdfGenerationType') as 'single' | 'multiple';
    if (savedType) {
      setPdfGenerationType(savedType);
    }
  }, []);

  // pdfGenerationType が変更されたら localStorage に保存
  useEffect(() => {
    localStorage.setItem('pdfGenerationType', pdfGenerationType);
  }, [pdfGenerationType]);

  const handlePdfSettingsSubmit = async () => {
    // leg_run 一覧・設定マスタの取得前/再取得中や設定未初期化の状態では、空/欠落・古い対象を送らない。
    if (legRunsIsLoading || legRunsIsFetching || settingIsLoading || legRunIds.length === 0) return;
    if (!legRunIds.every((id) => formState[id])) return;

    // 入力直後でも最新の編集内容を送るため、debounce 前の formState を使う。
    // ダイアログは選択中アンマウントされず前回分を保持しうるので、現在の legRunIds 分だけに絞る。
    const settings = Object.fromEntries(
      legRunIds
        .filter((legRunId) => formState[legRunId])
        .map((legRunId) => [legRunId, formState[legRunId]]),
    );

    // 対象が 1 件のとき生成方式の選択 UI は非表示なので、localStorage 由来の 'multiple' が
    // 残っていても single に補正し、1 件なのに ZIP で出力されるのを防ぐ。
    const effectiveType: 'single' | 'multiple' = legRunIds.length === 1 ? 'single' : pdfGenerationType;

    const entity: OutsourcingContractLegRunPdfRequestEntity = {
      outsourcingContractSettings: settings,
      legRunIds,
      preview: false,
      pdfGenerationType: effectiveType,
    };

    setGenerating(true);
    try {
      const result = await generate.mutateAsync(entity);

      // Content-Dispositionヘッダーからファイル名を取得
      const contentDisposition = String(result.headers['content-disposition']);
      let fileName = '';

      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          fileName = matches[1].replace(/['"]/g, '');
        }
      }

      if (!fileName) {
        fileName = `outsourcing_contract_${new Date().getTime()}.${effectiveType === 'single' ? 'pdf' : 'zip'}`;
      }

      // Blobを作成
      const blob = new Blob([result.data], {
        type: effectiveType === 'single'
          ? 'application/pdf'
          : 'application/zip'
      });

      // ダウンロードリンクを作成して自動クリック
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      handlePdfSettingsClose();
    } catch {
      enqueueSnackbar('エラーが発生しました。', { variant: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  type InputFieldMeta = {
    key: keyof OutsourcingContractPdfGenerationSetting;
    label: string;
    rows?: number;
    minRows?: number;
    type?: string;
  };

  const inputFields: InputFieldMeta[] = [
    {
      key: 'companyName',
      label: '会社名',
      rows: 1,
    },
    {
      key: 'branchName',
      label: '支社名',
      rows: 1,
    },
    {
      key: 'contactPerson',
      label: '担当者',
      rows: 1,
    },
    {
      key: 'companyAddress',
      label: '住所',
      rows: 1,
    },
    {
      key: 'companyPhoneNo',
      label: '電話番号',
      rows: 1,
    },
    {
      key: 'companyFaxNo',
      label: 'FAX番号',
      rows: 1,
    },
    {
      key: 'introduction',
      label: 'はじめに',
      minRows: 4,
    },
    {
      key: 'hasAttachment',
      label: '添付資料',
      type: 'radio',
    },
    {
      key: 'requiredMaterials',
      label: '必要資材',
      minRows: 4,
    },
    {
      key: 'aboutUnitPrice',
      label: '単価について',
      rows: 1,
    },
    {
      key: 'paymentTerm',
      label: '支払条件',
      minRows: 4,
    },
    {
      key: 'freeTextOneTitle',
      label: 'フリーテキスト1 タイトル',
      rows: 1,
    },
    {
      key: 'freeTextOneContent',
      label: 'フリーテキスト1 内容',
      minRows: 3,
    },
    {
      key: 'freeTextTwoTitle',
      label: 'フリーテキスト2 タイトル',
      rows: 1,
    },
    {
      key: 'freeTextTwoContent',
      label: 'フリーテキスト2 内容',
      minRows: 3,
    },
    {
      key: 'freeTextThreeContent',
      label: 'フリーテキスト3 内容',
      minRows: 3,
    },
    {
      key: 'freeTextFourContent',
      label: 'フリーテキスト4 内容',
      minRows: 3,
    },
  ];

  useEffect(() => {
    if (!downloadOutsourcingPdfSettingsIsOpen) return;
    if (!settingIsLoading && !setting) {
      enqueueSnackbar('委託表設定がありません。先に設定を作成してください。');
      handlePdfSettingsClose();
    }
  }, [downloadOutsourcingPdfSettingsIsOpen, enqueueSnackbar, handlePdfSettingsClose, setting, settingIsLoading]);

  // leg_run 一覧取得に失敗したら snackbar を表示して閉じる
  useEffect(() => {
    if (!downloadOutsourcingPdfSettingsIsOpen) return;
    if (legRunsIsError) {
      enqueueSnackbar('配車情報の取得に失敗しました。', { variant: 'error' });
      handlePdfSettingsClose();
    }
  }, [downloadOutsourcingPdfSettingsIsOpen, enqueueSnackbar, handlePdfSettingsClose, legRunsIsError]);

  // 対象の配車が存在しない場合は snackbar を表示して閉じる
  useEffect(() => {
    if (!downloadOutsourcingPdfSettingsIsOpen) return;
    // 再取得中はキャッシュ済みの空配列で誤って閉じないよう、完了を待つ。
    if (legRunsIsLoading || legRunsIsFetching) return;
    if (legRuns && legRuns.length === 0) {
      enqueueSnackbar('対象の配車がありません。');
      handlePdfSettingsClose();
    }
  }, [downloadOutsourcingPdfSettingsIsOpen, enqueueSnackbar, handlePdfSettingsClose, legRuns, legRunsIsLoading, legRunsIsFetching]);

  const currentLegRun = useMemo(
    () => (legRuns ?? []).find((legRun) => legRun.legRunId === currentLegRunId),
    [legRuns, currentLegRunId],
  );

  const currentLegRunLabel = useMemo(() => {
    if (!currentLegRun) return '';
    return `${currentLegRun.code} ${currentLegRun.loadingName ?? ''}→${currentLegRun.unloadingName ?? ''} ${currentLegRun.carrierCompany ?? ''}`;
  }, [currentLegRun]);

  const backdropMemo = useMemo(() => (
    <Backdrop
      sx={{ color: '#fff', zIndex: 100 }}
      open={generating}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ), [generating]);

  return (
    <Dialog
      open={downloadOutsourcingPdfSettingsIsOpen}
      onClose={handlePdfSettingsClose}
      sx={{ p: 0, m: 0, mt: 3, }}
      fullWidth
      fullScreen
    >
      <DialogTitle>
        委託表出力設定
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', p: 0, m: 0, }}>
          <Button
            onClick={() => {
              const currentIndex = legRunIds.indexOf(currentLegRunId);
              if (currentIndex > 0) {
                setCurrentLegRunId(legRunIds[currentIndex - 1]);
              }
            }}
            disabled={legRunIds.indexOf(currentLegRunId) <= 0}
          >
            前へ
          </Button>
          <Box>
            {[legRunIds.indexOf(currentLegRunId) + 1, '/', legRunIds.length].join(' ')}
          </Box>
          <Button
            onClick={() => {
              const currentIndex = legRunIds.indexOf(currentLegRunId);
              if (currentIndex < legRunIds.length - 1) {
                setCurrentLegRunId(legRunIds[currentIndex + 1]);
              }
            }}
            disabled={legRunIds.indexOf(currentLegRunId) === legRunIds.length - 1}
          >
            次へ
          </Button>
          <Typography variant="body1" sx={{ pl: 2 }}>
            {currentLegRunLabel}
          </Typography>
        </Box>
        <Box
          sx={{
            height: 'calc(100vh - 205px)',
            overflow: 'auto',
          }}
        >
          <Paper>
            <Stack spacing={2} direction="row" sx={{ width: '100%', p: 2 }} >
              {/* 左側: 入力フォーム */}
              <Stack sx={{ width: '50%' }} >
                <Paper sx={{ height: '100%', }}>
                  <Stack spacing={2} sx={{ m: 0, p: 2 }}>
                    {formState[currentLegRunId] && inputFields.map((field) => (
                      field.type === 'radio' ? (
                        <FormControl key={field.key} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ mb: 1, pr: 2 }}>{field.label}</Typography>
                          <RadioGroup
                            row
                            value={stringUtil.toText(formState[currentLegRunId][field.key])}
                            onChange={(e) => {
                              const value = e.target.value === 'true';
                              dispatch({ type: 'SET_FIELD', key: field.key, legRunId: currentLegRunId, value });
                              dispatchDebounced({ type: 'SET_FIELD', key: field.key, legRunId: currentLegRunId, value });
                            }}
                          >
                            <FormControlLabel value="true" control={<Radio />} label="有" />
                            <FormControlLabel value="false" control={<Radio />} label="無" />
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        <DebouncedTextField
                          key={field.key}
                          label={field.label}
                          orderId={currentLegRunId}
                          multiline={!(field.rows && field.rows === 1)}
                          size="small"
                          rows={field.rows}
                          minRows={field.minRows}
                          value={stringUtil.toText(formState[currentLegRunId][field.key])}
                          keyValue={field.key}
                          onValueChange={(val) => {
                            dispatch({ type: 'SET_FIELD', key: field.key, legRunId: currentLegRunId, value: val });
                          }}
                          onDebouncedChange={(val) => {
                            dispatchDebounced({ type: 'SET_FIELD', key: field.key, legRunId: currentLegRunId, value: val });
                          }}
                        />
                      )
                    ))}
                  </Stack>
                </Paper>
              </Stack>
              {/* 右側: プレビュー */}
              <Stack sx={{ width: '50%' }} >
                <Paper sx={{ p: 2, height: '100%', }}>
                  <iframe
                    ref={iframeRef}
                    srcDoc={previewHtml}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    title="preview"
                  />
                </Paper>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ pr: 3 }}>
        {legRunIds.length > 1 && (
          <Box>
            <FormControl component="fieldset">
              <RadioGroup
                value={pdfGenerationType}
                onChange={(e) => setPdfGenerationType(e.target.value as 'single' | 'multiple')}
                sx={{ p: 0, m: 0, }}
                row
              >
                <FormControlLabel
                  value="single"
                  control={<Radio />}
                  label="すべてをまとめた1枚のPDFを作成する"
                  sx={{ p: 0, m: 0, }}
                />
                <FormControlLabel
                  value="multiple"
                  control={<Radio />}
                  label="個別のPDFを作成する (ZIPでダウンロードする)"
                  sx={{ p: 0, m: 0, }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}
        <Button onClick={handlePdfSettingsClose}>キャンセル</Button>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <Button onClick={handlePdfSettingsSubmit}
          variant="contained"
          disabled={
            generating
            || legRunsIsLoading
            || legRunsIsFetching
            || settingIsLoading
            || legRunIds.length === 0
            || !legRunIds.every((id) => formState[id])
          }
        >
          作成する
        </Button>
      </DialogActions>
      {backdropMemo}
    </Dialog>
  );
});

export default PdfSettingDialog;
